import { useState } from "react";
import { Opportunity } from "@/opportunity/opportunity";
import { useOpportunityChangeStatusMutation, useOpportunityDeleteStatusChangeMutation } from "@/opportunity/api";

interface StatusChangeState {
  statusChangeId: number | null;
  statusName: string | null;
  showSuccessMessage: boolean;
  showUndoSuccessMessage: boolean;
  isUndoing: boolean;
}

export const useStatusChange = (opportunity: Opportunity) => {
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [successState, setSuccessState] = useState<StatusChangeState>({
    statusChangeId: null,
    statusName: null,
    showSuccessMessage: false,
    showUndoSuccessMessage: false,
    isUndoing: false,
  });

  const changeStatusMutation = useOpportunityChangeStatusMutation(opportunity);
  const deleteStatusChangeMutation = useOpportunityDeleteStatusChangeMutation(opportunity.id);

  const changeStatus = async (targetStatus: string, comments: string, reason?: string) => {
    setIsProcessing(targetStatus);

    try {
      const finalComments = comments.trim() ||
        `Status changed from ${opportunity.status} to ${targetStatus} via recommended action.`;

      const response = await changeStatusMutation.mutateAsync({
        opportunityId: opportunity.id,
        targetStatus,
        occurredOn: new Date(),
        comments: finalComments,
        reason,
      });

      // Show success message with undo option
      setSuccessState({
        statusChangeId: response.status_change_id,
        statusName: targetStatus,
        showSuccessMessage: true,
        showUndoSuccessMessage: false,
        isUndoing: false,
      });
    } catch (error) {
      console.error("Error changing status:", error);
      throw error;
    } finally {
      setIsProcessing(null);
    }
  };

  const undoStatusChange = async () => {
    if (!successState.statusChangeId) return;

    setSuccessState(prev => ({ ...prev, isUndoing: true }));

    try {
      await deleteStatusChangeMutation.mutateAsync(successState.statusChangeId);

      // Show undo success message
      setSuccessState(prev => ({
        ...prev,
        showSuccessMessage: false,
        showUndoSuccessMessage: true,
        isUndoing: false,
      }));
    } catch (error) {
      console.error("Error undoing status change:", error);
      setSuccessState(prev => ({ ...prev, isUndoing: false }));
      throw error;
    }
  };

  const closeSuccessMessage = () => {
    setSuccessState({
      statusChangeId: null,
      statusName: null,
      showSuccessMessage: false,
      showUndoSuccessMessage: false,
      isUndoing: false,
    });
  };

  const closeUndoSuccessMessage = () => {
    setSuccessState(prev => ({
      ...prev,
      showUndoSuccessMessage: false,
    }));
  };

  const showNextSteps = () => {
    closeSuccessMessage();
  };

  return {
    isProcessing,
    isError: changeStatusMutation.isError,
    changeStatus,
    undoStatusChange,
    closeSuccessMessage,
    closeUndoSuccessMessage,
    showNextSteps,
    successState,
  };
};